import { Check, Minus } from "lucide-react";

// 4 products from the official product matrix:
// Caddy (RT-CB01) · Caddy Pro (RT-SC01) · Caddy Vision (RT-SC02) · Cobot (RT-SC03)
const ROWS = [
    { feature: "RFID scanner", a: true, b: true, c: true, d: false },
    { feature: "Real-time tally", a: true, b: true, c: true, d: false },
    { feature: "Cashless UPI / Card payments", a: true, b: true, c: true, d: false },
    { feature: "Self-checkout", a: true, b: true, c: true, d: false },
    { feature: "Geofencing / Asset immobilisation", a: true, b: true, c: true, d: false },
    { feature: "Data lake integration", a: true, b: true, c: true, d: false },
    { feature: "Barcode scanning", a: false, b: true, c: true, d: false },
    { feature: "Load-cell weighing", a: false, b: true, c: true, d: false },
    { feature: "Theft protection", a: false, b: true, c: true, d: false },
    { feature: "AI vision scanner (no scanning)", a: false, b: false, c: true, d: true },
    { feature: "Motorised walk-along robot", a: false, b: false, c: false, d: true },
    { feature: "Generative AI shopping co-pilot", a: false, b: false, c: false, d: true },
    { feature: "Personalised user experience", a: false, b: false, c: false, d: true },
    { feature: "LLM + SLAM retail integration", a: false, b: false, c: false, d: true },
];

const STATUS = [
    { label: "In Testing", tone: "live" },
    { label: "In Development", tone: "dev" },
    { label: "Planned", tone: "planned" },
    { label: "Planned", tone: "planned" },
];

function Cell({ value }) {
    if (value === true) {
        return (
            <div className="flex justify-center">
                <Check className="w-4 h-4 text-white" />
            </div>
        );
    }
    if (value === false) {
        return (
            <div className="flex justify-center">
                <Minus className="w-4 h-4 text-rt-text3" />
            </div>
        );
    }
    return (
        <div className="text-center font-mono text-[13px] text-white/85">{value}</div>
    );
}

function StatusPill({ label, tone }) {
    const map = {
        live: "border-rt-teal/40 bg-rt-teal/10 text-rt-teal",
        dev: "border-amber-400/40 bg-amber-400/10 text-amber-300",
        planned: "border-white/15 bg-white/5 text-white/60",
    };
    return (
        <div
            className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase tracking-[0.18em] ${map[tone]}`}
        >
            {label}
        </div>
    );
}

export default function ComparisonTable() {
    return (
        <section
            id="compare"
            data-testid="comparison-table"
            className="relative bg-rt-bg py-24 lg:py-32"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="mb-14 grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7">
                        <div className="rt-eyebrow mb-4">SmartCaddy / Product matrix</div>
                        <h2 className="rt-h2 text-4xl md:text-5xl text-white">
                            Four products. One platform.
                        </h2>
                    </div>
                    <p className="lg:col-span-5 lg:pt-2 text-rt-text2 text-base max-w-lg">
                        Every product shares the same cloud, analytics, payments and OTA cadence.
                        Caddy is in test today; the rest of the line is in development or planned.
                        All status updates ship to our public roadmap.
                    </p>
                </div>

                <div className="overflow-x-auto -mx-6 lg:mx-0">
                    <div className="min-w-[920px] px-6 lg:px-0">
                        <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr] border-t border-white/15">
                            {/* Header row */}
                            <div className="py-5 text-xs font-mono uppercase tracking-[0.2em] text-rt-text3">
                                Feature
                            </div>
                            {[
                                { name: "Caddy", sku: "RT-CB01" },
                                { name: "Caddy Pro", sku: "RT-SC01" },
                                { name: "Caddy Vision", sku: "RT-SC02" },
                                { name: "Cobot", sku: "RT-SC03" },
                            ].map((p, i) => (
                                <div key={i} className="py-5 text-center">
                                    <div className="font-heading text-lg text-white">{p.name}</div>
                                    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-rt-text3 mt-1">
                                        {p.sku}
                                    </div>
                                    <div className="mt-2 flex justify-center">
                                        <StatusPill label={STATUS[i].label} tone={STATUS[i].tone} />
                                    </div>
                                </div>
                            ))}

                            {/* Feature rows */}
                            {ROWS.map((r, i) => (
                                <div className="contents" key={i}>
                                    <div className="py-4 border-t border-white/10 text-sm text-white/85">
                                        {r.feature}
                                    </div>
                                    <div className="py-4 border-t border-white/10 flex items-center justify-center">
                                        <Cell value={r.a} />
                                    </div>
                                    <div className="py-4 border-t border-white/10 flex items-center justify-center">
                                        <Cell value={r.b} />
                                    </div>
                                    <div className="py-4 border-t border-white/10 flex items-center justify-center">
                                        <Cell value={r.c} />
                                    </div>
                                    <div className="py-4 border-t border-white/10 flex items-center justify-center">
                                        <Cell value={r.d} />
                                    </div>
                                </div>
                            ))}

                            {/* Footer CTA row */}
                            <div className="py-5 border-t border-white/15" />
                            {["caddy", "caddy-pro", "caddy-vision", "cobot"].map((id, i) => (
                                <div key={id} className="py-5 border-t border-white/15 text-center">
                                    <a
                                        href={i === 0 ? "#contact" : "#contact"}
                                        data-testid={`compare-cta-${id}`}
                                        className="text-sm border border-white/20 px-4 py-2 inline-block hover:bg-white/5"
                                    >
                                        {i === 0 ? "Join the pilot" : "Get notified"}
                                    </a>
                                </div>
                            ))}
                        </div>

                        <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-rt-text3">
                            * Features listed reflect current product targets. Final specs may evolve as products move from test → development → release.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
